import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { cn } from "../lib/cn";
import { currentLocale, locales } from "../locale";

/**
 * A short list of actions under one control — the account control in a header, for one. With the dialog it is the only
 * thing that floats: sharp, a hairline border, the overlay shadow, no motion. Arrow keys move, Escape or a click outside
 * closes, and it opens on the page's side. The trigger is any `Button` passed `asChild`; its open state styles itself.
 */
export function Menu(props: Dropdown.DropdownMenuProps) {
  return <Dropdown.Root dir={locales[currentLocale()].dir} {...props} />;
}

export const MenuTrigger = Dropdown.Trigger;

export function MenuContent({ className, align = "end", sideOffset = 4, children, ...props }: Dropdown.DropdownMenuContentProps) {
  return (
    <Dropdown.Portal>
      <Dropdown.Content align={align} sideOffset={sideOffset} className={cn("z-50 min-w-[200px] border border-line-strong bg-surface py-1 text-ink shadow-overlay outline-none", className)} {...props}>
        {children}
      </Dropdown.Content>
    </Dropdown.Portal>
  );
}

/** One action: 31.5px tall (`h-9` on the 14px root), an icon beside its words. */
export function MenuItem({ className, ...props }: Dropdown.DropdownMenuItemProps) {
  return (
    <Dropdown.Item
      className={cn(
        "flex h-9 cursor-default select-none items-center gap-2 px-3 text-sm text-ink-2 outline-none transition-colors duration-150 ease-out-quick",
        "data-[highlighted]:bg-surface-2 data-[highlighted]:text-ink data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

/** Words that are not an action: who the menu is about. */
export function MenuLabel({ className, ...props }: Dropdown.DropdownMenuLabelProps) {
  return <Dropdown.Label className={cn("px-3 py-2", className)} {...props} />;
}

export function MenuSeparator({ className, ...props }: Dropdown.DropdownMenuSeparatorProps) {
  return <Dropdown.Separator className={cn("my-1 h-px bg-line", className)} {...props} />;
}
